import { apiGet, apiPatch, apiPost } from './client.js';

export type CustomerStatus = 'normal' | 'vip' | 'flagged';

export type CustomerStatusUpdate = {
  status: CustomerStatus;
  reason?: string | null;
  notes?: string | null;
};

export function updateCustomerStatus(id: string, body: CustomerStatusUpdate) {
  return apiPatch<Record<string, unknown>, CustomerStatusUpdate>(
    `/api/v1/customers/${id}/status`,
    body,
  ).then((r) => r.data);
}

export type CloneTicketBody = {
  customerId?: string | null;
  includeRepairs?: boolean;
  includeDevices?: boolean;
  includeNotes?: boolean;
  intakeType?: 'walk_in' | 'drop_off' | 'quote';
  notes?: string | null;
};

export function cloneTicket(id: string, body: CloneTicketBody = {}) {
  return apiPost<Record<string, unknown>, CloneTicketBody>(`/api/v1/tickets/${id}/clone`, body).then(
    (r) => r.data,
  );
}

export type UpsellSuggestionResult = {
  id: string;
  title: string;
  message: string;
  actionLabel: string | null;
  actionType: string | null;
  actionConfig: Record<string, unknown> | null;
  priority: number;
  mergeData?: Record<string, string>;
};

export type EvaluateUpsellBody = {
  ticketId?: string;
  customerId?: string | null;
  showAt: 'ticket_create' | 'checkout';
  devices?: Array<{
    modelId?: string | null;
    makeName?: string | null;
    modelName?: string | null;
    customModelName?: string | null;
  }>;
  repairs?: Array<{ serviceName: string; quotedPrice: number }>;
};

export function evaluateUpsell(body: EvaluateUpsellBody) {
  return apiPost<UpsellSuggestionResult[], EvaluateUpsellBody>('/api/v1/upsell/evaluate', body).then(
    (r) => r.data,
  );
}

export function interactUpsell(body: {
  suggestionId: string;
  ticketId?: string | null;
  customerId?: string | null;
  outcome: 'acted_on' | 'dismissed' | 'remind_at_checkout' | 'not_applicable';
  revenueAdded?: number;
}) {
  return apiPost('/api/v1/upsell/interact', body).then((r) => r.data);
}

export function listUpsellSuggestions() {
  return apiGet<Array<Record<string, unknown>>>('/api/v1/upsell/suggestions').then((r) => r.data);
}

export function createUpsellSuggestion(body: Record<string, unknown>) {
  return apiPost('/api/v1/upsell/suggestions', body).then((r) => r.data);
}

export function patchUpsellSuggestion(id: string, body: Record<string, unknown>) {
  return apiPatch(`/api/v1/upsell/suggestions/${id}`, body).then((r) => r.data);
}

export function getUpsellReport(params?: { period?: string; storeId?: string }) {
  const q = new URLSearchParams();
  if (params?.period) q.set('period', params.period);
  if (params?.storeId) q.set('storeId', params.storeId);
  const qs = q.toString();
  return apiGet<Record<string, unknown>>(`/api/v1/upsell/report${qs ? `?${qs}` : ''}`).then(
    (r) => r.data,
  );
}

export function acknowledgeFlaggedCustomer(customerId: string, context: string) {
  return apiPost(`/api/v1/customers/${customerId}/flag-acknowledge`, { context }).then(
    (r) => r.data,
  );
}
